import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { ChannelIcon } from '@/components/ui/channel-icon';
import { Typography } from '@/components/ui/typography';
import { useBudgetType } from '@/hooks/use-budget-type';
import { useCurrencySymbol } from '@/hooks/use-currency-symbol';
import { formatShortDate } from '@/utils/date-util';

import { ChannelFormState } from '../types';

function Separator() {
  return <span className="bg-fx-neutral-400 size-[3px] rounded-full" />;
}

interface ChannelRowProps {
  channel: ChannelFormState;
  openingId: string;
}

export function ChannelRow({ channel, openingId }: ChannelRowProps) {
  const intl = useIntl();
  const { formatCurrency } = useCurrencySymbol();
  const { calculateTotalBudget } = useBudgetType();

  // Calculate the channel budget based on the budget type and duration
  const channelBudget = calculateTotalBudget({
    budgetType: channel.budgetType,
    budgetPerUnit: channel.openingBudgets[openingId],
    startDate: channel.startsAt,
    endDate: channel.endAt,
  });

  const isContract = channel.vonqDetails?.contractId != null;

  const displayBudget = useMemo(() => {
    if (channel.isFree)
      return intl.formatMessage({
        defaultMessage: 'Free',
        description: 'Free budget',
      });

    if (isContract)
      return intl.formatMessage({
        defaultMessage: 'As per your contract',
        description: 'Placeholder for contract budget',
      });

    if (!channelBudget) return '-';

    return formatCurrency(channelBudget);
  }, [channel.isFree, channelBudget, formatCurrency, intl]);

  return (
    <li className="flex items-center justify-between gap-2">
      <div className="flex min-w-0 items-center gap-2">
        {/* Channel icon */}
        <ChannelIcon
          channelSlug={channel.slug}
          variant="large"
          size="xs"
          logoSquareUrl={channel.vonqDetails?.logoSquareUrl}
          logoRectangleUrl={channel.vonqDetails?.logoRectangleUrl}
          channelName={channel.name}
          displayTitle={channel.displayTitle}
          titleClassName="text-fx-neutral-600 font-normal"
          className="min-w-0"
        />

        <Separator />

        {/* Duration */}
        <Typography variant="small1" className="text-fx-neutral-600 shrink-0">
          {channel.startsAt ? formatShortDate(channel.startsAt) : 'Today'} -{' '}
          {channel.endAt ? formatShortDate(channel.endAt) : 'Ongoing'}
        </Typography>
      </div>

      {/* Channel budget */}
      <Typography
        variant="small1"
        className={`text-fx-neutral-600 shrink-0 ${isContract ? 'italic' : ''}`}
      >
        {displayBudget}
      </Typography>
    </li>
  );
}
